import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Crown, DollarSign, Swords, Users, Bot, Trophy, Clock, Flag, X, Eye, EyeOff } from 'lucide-react';
import { Chessboard } from 'react-chessboard';
import { Chess as ChessJS } from 'chess.js';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Custom SVG chess pieces - original designs inspired by classic style
const PIECE_SVGS = {
  wK: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 45 45"><g fill="#fff" stroke="#000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22.5 11.63V6M20 8h5" stroke-linejoin="miter"/><path d="M22.5 25s4.5-7.5 3-10.5c0 0-1-2.5-3-2.5s-3 2.5-3 2.5c-1.5 3 3 10.5 3 10.5" fill="#fff" stroke-linecap="butt" stroke-linejoin="miter"/><path d="M12.5 37c5.5 3.5 14.5 3.5 20 0v-7s9-4.5 6-10.5c-4-6.5-13.5-3.5-16 4V27v-3.5c-2.5-7.5-12-10.5-16-4-3 6 6 10.5 6 10.5v7" fill="#fff"/><path d="M12.5 30c5.5-3 14.5-3 20 0M12.5 33.5c5.5-3 14.5-3 20 0M12.5 37c5.5-3 14.5-3 20 0"/></g></svg>`,
  wQ: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 45 45"><g fill="#fff" stroke="#000" stroke-width="1.5" stroke-linejoin="round"><path d="M9 26c8.5-1.5 21-1.5 27 0l2.5-12.5L31 25l-.3-14.1-5.2 13.6-3-14.5-3 14.5-5.2-13.6L14 25 6.5 13.5 9 26z"/><path d="M9 26c0 2 1.5 2 2.5 4 1 1.5 1 1 .5 3.5-1.5 1-1 2.5-1 2.5-1.5 1.5 0 2.5 0 2.5 6.5 1 16.5 1 23 0 0 0 1.5-1 0-2.5 0 0 .5-1.5-1-2.5-.5-2.5-.5-2 .5-3.5 1-2 2.5-2 2.5-4-8.5-1.5-18.5-1.5-27 0z"/><path d="M11.5 30c3.5-1 18.5-1 22 0M12 33.5c6-1 15-1 21 0" fill="none"/><circle cx="6" cy="12" r="2"/><circle cx="14" cy="9" r="2"/><circle cx="22.5" cy="8" r="2"/><circle cx="31" cy="9" r="2"/><circle cx="39" cy="12" r="2"/></g></svg>`,
  wR: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 45 45"><g fill="#fff" stroke="#000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 39h27v-3H9v3zM12 36v-4h21v4H12zM11 14V9h4v2h5V9h5v2h5V9h4v5" stroke-linecap="butt"/><path d="M34 14l-3 3H14l-3-3"/><path d="M31 17v12.5H14V17" stroke-linecap="butt" stroke-linejoin="miter"/><path d="M31 29.5l1.5 2.5h-20l1.5-2.5"/><path d="M11 14h23" fill="none" stroke-linejoin="miter"/></g></svg>`,
  wB: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 45 45"><g fill="none" stroke="#000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><g fill="#fff" stroke-linecap="butt"><path d="M9 36c3.39-.97 10.11.43 13.5-2 3.39 2.43 10.11 1.03 13.5 2 0 0 1.65.54 3 2-.68.97-1.65.99-3 .5-3.39-.97-10.11.46-13.5-1-3.39 1.46-10.11.03-13.5 1-1.35.49-2.32.47-3-.5 1.35-1.46 3-2 3-2z"/><path d="M15 32c2.5 2.5 12.5 2.5 15 0 .5-1.5 0-2 0-2 0-2.5-2.5-4-2.5-4 5.5-1.5 6-11.5-5-15.5-11 4-10.5 14-5 15.5 0 0-2.5 1.5-2.5 4 0 0-.5.5 0 2z"/><path d="M25 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 1 1 5 0z"/></g><path d="M17.5 26h10M15 30h15m-7.5-14.5v5M20 18h5" stroke-linejoin="miter"/></g></svg>`,
  wN: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 45 45"><g fill="none" stroke="#000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 10c10.5 1 16.5 8 16 29H15c0-9 10-6.5 8-21" fill="#fff"/><path d="M24 18c.38 2.91-5.55 7.37-8 9-3 2-2.82 4.34-5 4-1.042-.94 1.41-3.04 0-3-1 0 .19 1.23-1 2-1 0-4.003 1-4-4 0-2 6-12 6-12s1.89-1.9 2-3.5c-.73-.994-.5-2-.5-3 1-1 3 2.5 3 2.5h2s.78-1.992 2.5-3c1 0 1 3 1 3" fill="#fff"/><path d="M9.5 25.5a.5.5 0 1 1-1 0 .5.5 0 1 1 1 0z" fill="#000"/><path d="M14.933 15.75a.5 1.5 30 1 1-.866-.5.5 1.5 30 1 1 .866.5z" fill="#000" stroke-width="1.49997"/></g></svg>`,
  wP: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 45 45"><path d="M22.5 9c-2.21 0-4 1.79-4 4 0 .89.29 1.71.78 2.38C17.33 16.5 16 18.59 16 21c0 2.03.94 3.84 2.41 5.03-3 1.06-7.41 5.55-7.41 13.47h23c0-7.92-4.41-12.41-7.41-13.47 1.47-1.19 2.41-3 2.41-5.03 0-2.41-1.33-4.5-3.28-5.62.49-.67.78-1.49.78-2.38 0-2.21-1.79-4-4-4z" fill="#fff" stroke="#000" stroke-width="1.5" stroke-linecap="round"/></svg>`,
  bK: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 45 45"><g fill="none" stroke="#000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22.5 11.63V6" stroke-linejoin="miter"/><path d="M22.5 25s4.5-7.5 3-10.5c0 0-1-2.5-3-2.5s-3 2.5-3 2.5c-1.5 3 3 10.5 3 10.5" fill="#000" stroke-linecap="butt" stroke-linejoin="miter"/><path d="M12.5 37c5.5 3.5 14.5 3.5 20 0v-7s9-4.5 6-10.5c-4-6.5-13.5-3.5-16 4V27v-3.5c-2.5-7.5-12-10.5-16-4-3 6 6 10.5 6 10.5v7" fill="#000"/><path d="M20 8h5" stroke-linejoin="miter"/><path d="M12.5 30c5.5-3 14.5-3 20 0m-20 3.5c5.5-3 14.5-3 20 0m-20 3.5c5.5-3 14.5-3 20 0" stroke="#fff"/></g></svg>`,
  bQ: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 45 45"><g fill="#000" stroke="#000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><g stroke="none"><circle cx="6" cy="12" r="2.75"/><circle cx="14" cy="9" r="2.75"/><circle cx="22.5" cy="8" r="2.75"/><circle cx="31" cy="9" r="2.75"/><circle cx="39" cy="12" r="2.75"/></g><path d="M9 26c8.5-1.5 21-1.5 27 0l2.5-12.5L31 25l-.3-14.1-5.2 13.6-3-14.5-3 14.5-5.2-13.6L14 25 6.5 13.5 9 26z" stroke-linecap="butt"/><path d="M9 26c0 2 1.5 2 2.5 4 1 1.5 1 1 .5 3.5-1.5 1-1 2.5-1 2.5-1.5 1.5 0 2.5 0 2.5 6.5 1 16.5 1 23 0 0 0 1.5-1 0-2.5 0 0 .5-1.5-1-2.5-.5-2.5-.5-2 .5-3.5 1-2 2.5-2 2.5-4-8.5-1.5-18.5-1.5-27 0z" stroke-linecap="butt"/><path d="M11 38.5a35 35 1 0 0 23 0" fill="none" stroke-linecap="butt"/><path d="M11 29a35 35 1 0 1 23 0m-21.5 2.5h20m-21 3h21" fill="none" stroke="#fff"/></g></svg>`,
  bR: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 45 45"><g fill="#000" stroke="#000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 39h27v-3H9v3zm3-3v-4h21v4H12zm-1-22V9h4v2h5V9h5v2h5V9h4v5" stroke-linecap="butt"/><path d="M34 14l-3 3H14l-3-3"/><path d="M31 17v12.5H14V17" stroke-linecap="butt" stroke-linejoin="miter"/><path d="M31 29.5l1.5 2.5h-20l1.5-2.5"/><path d="M11 14h23" fill="none" stroke-linejoin="miter"/><path d="M12 35.5h21m-20-4h19m-18-2h17" fill="none" stroke="#fff" stroke-width="1" stroke-linejoin="miter"/></g></svg>`,
  bB: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 45 45"><g fill="none" stroke="#000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><g fill="#000" stroke-linecap="butt"><path d="M9 36c3.39-.97 10.11.43 13.5-2 3.39 2.43 10.11 1.03 13.5 2 0 0 1.65.54 3 2-.68.97-1.65.99-3 .5-3.39-.97-10.11.46-13.5-1-3.39 1.46-10.11.03-13.5 1-1.35.49-2.32.47-3-.5 1.35-1.46 3-2 3-2z"/><path d="M15 32c2.5 2.5 12.5 2.5 15 0 .5-1.5 0-2 0-2 0-2.5-2.5-4-2.5-4 5.5-1.5 6-11.5-5-15.5-11 4-10.5 14-5 15.5 0 0-2.5 1.5-2.5 4 0 0-.5.5 0 2z"/><path d="M25 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 1 1 5 0z"/></g><path d="M17.5 26h10M15 30h15m-7.5-14.5v5M20 18h5" stroke="#fff" stroke-linejoin="miter"/></g></svg>`,
  bN: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 45 45"><g fill="none" stroke="#000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 10c10.5 1 16.5 8 16 29H15c0-9 10-6.5 8-21" fill="#000"/><path d="M24 18c.38 2.91-5.55 7.37-8 9-3 2-2.82 4.34-5 4-1.042-.94 1.41-3.04 0-3-1 0 .19 1.23-1 2-1 0-4.003 1-4-4 0-2 6-12 6-12s1.89-1.9 2-3.5c-.73-.994-.5-2-.5-3 1-1 3 2.5 3 2.5h2s.78-1.992 2.5-3c1 0 1 3 1 3" fill="#000"/><path d="M9.5 25.5a.5.5 0 1 1-1 0 .5.5 0 1 1 1 0z" fill="#fff" stroke="#fff"/><path d="M14.933 15.75a.5 1.5 30 1 1-.866-.5.5 1.5 30 1 1 .866.5z" fill="#fff" stroke="#fff" stroke-width="1.49997"/><path d="M24.55 10.4l-.45 1.45.5.15c3.15 1 5.65 2.49 7.9 6.75S35.75 29.06 35.25 39l-.05.5h2.25l.05-.5c.5-10.06-.88-16.85-3.25-21.34-2.37-4.49-5.79-6.64-9.19-7.16l-.51-.1z" fill="#fff" stroke="none"/></g></svg>`,
  bP: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 45 45"><path d="M22.5 9c-2.21 0-4 1.79-4 4 0 .89.29 1.71.78 2.38C17.33 16.5 16 18.59 16 21c0 2.03.94 3.84 2.41 5.03-3 1.06-7.41 5.55-7.41 13.47h23c0-7.92-4.41-12.41-7.41-13.47 1.47-1.19 2.41-3 2.41-5.03 0-2.41-1.33-4.5-3.28-5.62.49-.67.78-1.49.78-2.38 0-2.21-1.79-4-4-4z" fill="#000" stroke="#000" stroke-width="1.5" stroke-linecap="round"/></svg>`,
};

// Custom piece renderer using our original SVG designs
const customPieces = () => {
  const pieces = {};
  const pieceMap = {
    wK: 'wK', wQ: 'wQ', wR: 'wR', wB: 'wB', wN: 'wN', wP: 'wP',
    bK: 'bK', bQ: 'bQ', bR: 'bR', bB: 'bB', bN: 'bN', bP: 'bP'
  };
  
  Object.entries(pieceMap).forEach(([key, svgKey]) => {
    pieces[key] = ({ squareWidth }) => (
      <div
        style={{
          width: squareWidth,
          height: squareWidth,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none'
        }}
        dangerouslySetInnerHTML={{ __html: PIECE_SVGS[svgKey] }}
      />
    );
  });
  
  return pieces;
};

// Reusable Chess Board with click-to-move and hints
function GameChessboard({ 
  chess, 
  onMove, 
  orientation = 'white',
  showHints = true,
  disabled = false 
}) {
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [legalMoves, setLegalMoves] = useState([]);
  const [lastMove, setLastMove] = useState(null);

  // Get legal moves for selected piece
  useEffect(() => {
    if (selectedSquare && chess && showHints) {
      try {
        const moves = chess.moves({ square: selectedSquare, verbose: true });
        setLegalMoves(moves.map(m => m.to));
      } catch (e) {
        setLegalMoves([]);
      }
    } else {
      setLegalMoves([]);
    }
  }, [selectedSquare, chess, showHints]);

  // Clear selection when chess position changes (opponent moved)
  useEffect(() => {
    setSelectedSquare(null);
    setLegalMoves([]);
  }, [chess?.fen()]);

  // Handle square click (click-to-move)
  const handleSquareClick = async ({ piece, square }) => {
    console.log('Square clicked:', square, 'piece:', piece, 'disabled:', disabled);
    if (disabled || !chess) return;
    
    const boardPiece = chess.get(square);
    const turn = chess.turn();
    console.log('Turn:', turn, 'boardPiece:', boardPiece);
    
    // If a piece is already selected
    if (selectedSquare) {
      // Clicked same square - deselect
      if (selectedSquare === square) {
        setSelectedSquare(null);
        setLegalMoves([]);
        return;
      }
      
      // Try to make the move if it's a legal destination
      if (legalMoves.includes(square)) {
        try {
          const result = await onMove(selectedSquare, square);
          if (result !== false) {
            setLastMove({ from: selectedSquare, to: square });
          }
        } catch (e) {
          console.error('Move error:', e);
        }
        setSelectedSquare(null);
        setLegalMoves([]);
        return;
      }
      
      // Clicked on a different friendly piece - switch selection
      if (boardPiece && boardPiece.color === turn) {
        setSelectedSquare(square);
        return;
      }
      
      // Clicked on empty or enemy square that's not a legal move - deselect
      setSelectedSquare(null);
      setLegalMoves([]);
    } else {
      // No piece selected - select if it's a friendly piece
      if (boardPiece && boardPiece.color === turn) {
        setSelectedSquare(square);
      }
    }
  };

  // Handle piece click - just delegate to square click
  const handlePieceClick = ({ piece, square }) => {
    handleSquareClick({ piece, square });
  };

  // Handle piece drop (drag-and-drop)
  const handlePieceDrop = async (sourceSquare, targetSquare, piece) => {
    setSelectedSquare(null);
    setLegalMoves([]);
    
    try {
      const result = await onMove(sourceSquare, targetSquare, piece);
      if (result !== false) {
        setLastMove({ from: sourceSquare, to: targetSquare });
        return true;
      }
    } catch (e) {
      console.error('Drop error:', e);
    }
    return false;
  };

  // Custom square styles for hints and selection
  const customSquareStyles = useMemo(() => {
    const styles = {};
    
    // Highlight selected square
    if (selectedSquare) {
      styles[selectedSquare] = {
        backgroundColor: 'rgba(255, 255, 0, 0.5)',
        boxShadow: 'inset 0 0 0 4px rgba(255, 255, 0, 0.8)'
      };
    }
    
    // Highlight legal moves
    if (showHints && legalMoves.length > 0) {
      legalMoves.forEach(sq => {
        const isCapture = chess?.get(sq);
        if (isCapture) {
          // Capture indicator - ring around the square
          styles[sq] = {
            ...styles[sq],
            background: 'radial-gradient(circle, transparent 55%, rgba(0, 0, 0, 0.4) 55%, rgba(0, 0, 0, 0.4) 70%, transparent 70%)',
            cursor: 'pointer'
          };
        } else {
          // Move indicator - dot in center
          styles[sq] = {
            ...styles[sq],
            background: 'radial-gradient(circle, rgba(0, 0, 0, 0.3) 20%, transparent 20%)',
            cursor: 'pointer'
          };
        }
      });
    }
    
    // Highlight last move
    if (lastMove) {
      styles[lastMove.from] = {
        ...styles[lastMove.from],
        backgroundColor: styles[lastMove.from]?.backgroundColor || 'rgba(255, 255, 0, 0.2)'
      };
      styles[lastMove.to] = {
        ...styles[lastMove.to],
        backgroundColor: styles[lastMove.to]?.backgroundColor || 'rgba(255, 255, 0, 0.35)'
      };
    }
    
    return styles;
  }, [selectedSquare, legalMoves, showHints, lastMove, chess]);

  const pieces = useMemo(() => customPieces(), []);

  return (
    <Chessboard
      options={{
        position: chess?.fen() || 'start',
        onPieceDrop: handlePieceDrop,
        onSquareClick: handleSquareClick,
        onPieceClick: handlePieceClick,
        boardOrientation: orientation,
        boardStyle: {
          borderRadius: '4px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)'
        },
        darkSquareStyle: { backgroundColor: '#769656' },
        lightSquareStyle: { backgroundColor: '#eeeed2' },
        squareStyles: customSquareStyles,
        pieces: pieces,
        animationDurationInMs: 150,
        allowDragging: !disabled
      }}
    />
  );
}

// Hints Toggle Component
function HintsToggle({ enabled, onChange }) {
  return (
    <div className="flex items-center gap-3 bg-slate-800/50 rounded-lg px-3 py-2">
      <span className="text-slate-400 text-sm">Hints</span>
      <button
        onClick={() => onChange(!enabled)}
        className={`relative w-12 h-6 rounded-full transition-colors ${
          enabled ? 'bg-green-500' : 'bg-slate-600'
        }`}
      >
        <div
          className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-7' : 'translate-x-1'
          }`}
        />
      </button>
      {enabled ? (
        <Eye className="w-4 h-4 text-green-400" />
      ) : (
        <EyeOff className="w-4 h-4 text-slate-500" />
      )}
    </div>
  );
}

// Calibration View Component
function CalibrationView({ user, token, onComplete, chessRating }) {
  const [game, setGame] = useState(null);
  const [chess, setChess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gameResult, setGameResult] = useState(null);
  const [showHints, setShowHints] = useState(true);
  
  // Chess.com import state
  const [showImport, setShowImport] = useState(false);
  const [chessComUsername, setChessComUsername] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifiedAccount, setVerifiedAccount] = useState(null);
  const [importing, setImporting] = useState(false);
  const [verificationCode, setVerificationCode] = useState(null);
  const [verificationStep, setVerificationStep] = useState(1); // 1: enter username, 2: verify code

  const startCalibrationGame = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/games/chess/p2p/calibration/start`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const newChess = new ChessJS(response.data.fen);
      setChess(newChess);
      setGame(response.data);
      setGameResult(null);
      
      if (response.data.resumed) {
        toast.info('Resuming your game');
      } else {
        toast.success(`Game ${response.data.game_number} of 5 started!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start game');
    } finally {
      setLoading(false);
    }
  };

  const verifyChessComAccount = async () => {
    if (!chessComUsername.trim()) {
      toast.error('Enter a Chess.com username');
      return;
    }
    
    setVerifying(true);
    setVerifiedAccount(null);
    
    try {
      // First check if user exists
      const verifyResponse = await axios.get(
        `${API_URL}/api/games/chess/p2p/verify-chesscom/${encodeURIComponent(chessComUsername.trim())}`
      );
      
      if (!verifyResponse.data.exists) {
        toast.error(verifyResponse.data.error || 'User not found');
        setVerifying(false);
        return;
      }
      
      if (!verifyResponse.data.can_import) {
        toast.warning(`Need at least 25 rated games. You have ${verifyResponse.data.total_games}.`);
        setVerifying(false);
        return;
      }
      
      // Start verification process
      const startResponse = await axios.post(
        `${API_URL}/api/games/chess/p2p/start-verification`,
        { chess_com_username: chessComUsername.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setVerifiedAccount(verifyResponse.data);
      setVerificationCode(startResponse.data.verification_code);
      setVerificationStep(2);
      toast.success('Add the code to your Chess.com profile location');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start verification');
    } finally {
      setVerifying(false);
    }
  };

  const importFromChessCom = async () => {
    if (!verifiedAccount || !verificationCode) return;
    
    setImporting(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/games/chess/p2p/import-chesscom`,
        { chess_com_username: verifiedAccount.username },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Imported! Your rating: ${response.data.site_rating}`);
      setTimeout(() => onComplete(), 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed - make sure the code is in your profile location');
    } finally {
      setImporting(false);
    }
  };

  const makeMove = async (sourceSquare, targetSquare, piece) => {
    if (!game || !chess) return false;
    
    // Check if it's player's turn
    const isWhiteTurn = chess.turn() === 'w';
    const playerIsWhite = game.player_color === 'white';
    
    if (isWhiteTurn !== playerIsWhite) {
      return false;
    }

    // Try the move locally first
    const move = chess.move({
      from: sourceSquare,
      to: targetSquare,
      promotion: piece?.[1]?.toLowerCase() || 'q'
    });

    if (!move) return false;

    // Revert local move - we'll apply server response
    chess.undo();

    try {
      const response = await axios.post(
        `${API_URL}/api/games/chess/p2p/calibration/move`,
        { 
          game_id: game.game_id, 
          move: `${sourceSquare}${targetSquare}${piece?.[1]?.toLowerCase() === 'q' ? '' : piece?.[1]?.toLowerCase() || ''}` 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Apply the move
      const newChess = new ChessJS(response.data.fen);
      setChess(newChess);

      if (response.data.game_over) {
        setGameResult({
          result: response.data.result,
          calibrationComplete: response.data.calibration_complete,
          gamesPlayed: response.data.calibration_games_played,
          rating: response.data.rating
        });

        if (response.data.calibration_complete) {
          toast.success(`Calibration complete! Your rating: ${response.data.rating}`);
          setTimeout(() => onComplete(), 2000);
        } else {
          const resultText = response.data.result === 'win' ? 'You won!' : 
                           response.data.result === 'draw' ? 'Draw!' : 'You lost!';
          toast.info(resultText);
        }
      }

      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid move');
      return false;
    }
  };

  const gamesPlayed = chessRating?.calibration_games_played || 0;
  const gamesRemaining = 5 - gamesPlayed;

  // Chess.com Import Modal
  if (showImport) {
    return (
      <div className="max-w-xl mx-auto">
        <div className="bg-slate-900/50 rounded-2xl p-8 border border-slate-800">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <img src="https://images.chesscomfiles.com/uploads/v1/images_users/tiny_mce/SamCopeland/phpmeXx6V.png" alt="Chess.com" className="w-6 h-6" />
              Link Chess.com Account
            </h2>
            <button onClick={() => { setShowImport(false); setVerificationStep(1); setVerifiedAccount(null); setVerificationCode(null); }} className="text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          {verificationStep === 1 ? (
            <>
              <p className="text-slate-400 mb-6">
                Link your Chess.com account to skip calibration games. We'll verify ownership and import your rating.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="text-slate-400 text-sm mb-2 block">Chess.com Username</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={chessComUsername}
                      onChange={(e) => setChessComUsername(e.target.value)}
                      placeholder="e.g. magnuscarlsen"
                      className="flex-1 bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder:text-slate-500"
                      onKeyDown={(e) => e.key === 'Enter' && verifyChessComAccount()}
                    />
                    <button
                      onClick={verifyChessComAccount}
                      disabled={verifying || !chessComUsername.trim()}
                      className="px-6 py-3 bg-green-600 hover:bg-green-500 disabled:bg-slate-700 rounded-xl text-white font-medium"
                    >
                      {verifying ? 'Checking...' : 'Link Account'}
                    </button>
                  </div>
                </div>

                <p className="text-slate-500 text-xs text-center">
                  Requires 25+ rated games on Chess.com
                </p>
              </div>
            </>
          ) : (
            <>
              {verifiedAccount && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4"
                >
                  <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                    <div className="flex items-center gap-4 mb-4">
                      {verifiedAccount.avatar && (
                        <img src={verifiedAccount.avatar} alt="" className="w-12 h-12 rounded-full" />
                      )}
                      <div>
                        <p className="text-white font-semibold">{verifiedAccount.username}</p>
                        <p className="text-slate-400 text-sm">{verifiedAccount.total_games} games played</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      {verifiedAccount.ratings.blitz && (
                        <div className="bg-slate-900/50 rounded-lg p-2 text-center">
                          <p className="text-slate-400 text-xs">Blitz</p>
                          <p className="text-yellow-400 font-bold">{verifiedAccount.ratings.blitz}</p>
                        </div>
                      )}
                      {verifiedAccount.ratings.rapid && (
                        <div className="bg-slate-900/50 rounded-lg p-2 text-center">
                          <p className="text-slate-400 text-xs">Rapid</p>
                          <p className="text-blue-400 font-bold">{verifiedAccount.ratings.rapid}</p>
                        </div>
                      )}
                      {verifiedAccount.ratings.bullet && (
                        <div className="bg-slate-900/50 rounded-lg p-2 text-center">
                          <p className="text-slate-400 text-xs">Bullet</p>
                          <p className="text-red-400 font-bold">{verifiedAccount.ratings.bullet}</p>
                        </div>
                      )}
                      {verifiedAccount.ratings.daily && (
                        <div className="bg-slate-900/50 rounded-lg p-2 text-center">
                          <p className="text-slate-400 text-xs">Daily</p>
                          <p className="text-green-400 font-bold">{verifiedAccount.ratings.daily}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                    <h3 className="text-yellow-400 font-semibold mb-2">Verify Account Ownership</h3>
                    <p className="text-slate-300 text-sm mb-3">
                      Add this code to your Chess.com profile <strong>Location</strong> field:
                    </p>
                    <div className="bg-slate-900 rounded-lg p-3 flex items-center justify-between">
                      <code className="text-green-400 font-mono text-lg">{verificationCode}</code>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(verificationCode);
                          toast.success('Code copied!');
                        }}
                        className="px-3 py-1 bg-slate-700 hover:bg-slate-600 rounded text-sm text-white"
                      >
                        Copy
                      </button>
                    </div>
                    <p className="text-slate-400 text-xs mt-3">
                      Go to <a href="https://www.chess.com/settings" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Chess.com Settings</a> → Edit Profile → Location
                    </p>
                  </div>

                  <button
                    onClick={importFromChessCom}
                    disabled={importing}
                    className="w-full py-3 bg-green-600 hover:bg-green-500 disabled:bg-slate-700 rounded-xl text-white font-bold"
                  >
                    {importing ? 'Verifying...' : "I've Added the Code - Verify & Import"}
                  </button>

                  <button
                    onClick={() => { setVerificationStep(1); setVerifiedAccount(null); setVerificationCode(null); }}
                    className="w-full py-2 text-slate-400 hover:text-white text-sm"
                  >
                    Use a different account
                  </button>
                </motion.div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-3 mb-4">
          <Bot className="w-8 h-8 text-blue-500" />
          <h1 className="text-3xl font-bold text-white">Skill Calibration</h1>
        </div>
        <p className="text-slate-400">
          Play {gamesRemaining} more game{gamesRemaining !== 1 ? 's' : ''} against our bot to get your rating
        </p>
        <div className="flex justify-center gap-2 mt-4">
          {[1, 2, 3, 4, 5].map((num) => (
            <div
              key={num}
              className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                num <= gamesPlayed
                  ? 'bg-green-500 text-white'
                  : num === gamesPlayed + 1 && game
                  ? 'bg-blue-500 text-white animate-pulse'
                  : 'bg-slate-700 text-slate-400'
              }`}
            >
              {num}
            </div>
          ))}
        </div>
      </div>

      {!game ? (
        <div className="space-y-4">
          <div className="bg-slate-900/50 rounded-2xl p-8 border border-slate-800 text-center">
            <Bot className="w-20 h-20 mx-auto text-slate-600 mb-6" />
            <h2 className="text-xl font-semibold text-white mb-4">Ready for Game {gamesPlayed + 1}?</h2>
            <p className="text-slate-400 mb-6">
              {gamesPlayed === 0 
                ? "You'll play 5 games against our bot. Each game gets progressively harder to assess your skill level."
                : `${gamesRemaining} game${gamesRemaining !== 1 ? 's' : ''} remaining. Keep playing to complete calibration!`
              }
            </p>
            <button
              onClick={startCalibrationGame}
              disabled={loading}
              className="px-8 py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 rounded-xl text-white font-bold transition-colors"
            >
              {loading ? 'Starting...' : 'Start Game'}
            </button>
          </div>

          {/* Chess.com Import Option - only show if no games played yet */}
          {gamesPlayed === 0 && (
            <div className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 rounded-2xl p-6 border border-green-500/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                    <img 
                      src="https://images.chesscomfiles.com/uploads/v1/images_users/tiny_mce/SamCopeland/phpmeXx6V.png" 
                      alt="Chess.com" 
                      className="w-8 h-8"
                    />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Have a Chess.com account?</h3>
                    <p className="text-slate-400 text-sm">Import your rating and skip calibration</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowImport(true)}
                  className="px-6 py-2 bg-green-600 hover:bg-green-500 rounded-xl text-white font-medium transition-colors"
                >
                  Import Rating
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-slate-900/50 rounded-2xl p-4 border border-slate-800">
              <div className="flex justify-end mb-2">
                <HintsToggle enabled={showHints} onChange={setShowHints} />
              </div>
              <div className="max-w-md mx-auto">
                <GameChessboard
                  chess={chess}
                  onMove={makeMove}
                  orientation={game.player_color}
                  showHints={showHints}
                  disabled={gameResult !== null}
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-slate-900/50 rounded-2xl p-6 border border-slate-800">
              <h3 className="text-lg font-semibold text-white mb-4">Game Info</h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Game</span>
                  <span className="text-white">{game.game_number} of 5</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Your Color</span>
                  <span className={game.player_color === 'white' ? 'text-white' : 'text-slate-300'}>
                    {game.player_color === 'white' ? '♔ White' : '♚ Black'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Bot Difficulty</span>
                  <span className="text-yellow-400">
                    {'★'.repeat(game.bot_depth > 4 ? 5 : game.bot_depth)}
                    {'☆'.repeat(5 - (game.bot_depth > 4 ? 5 : game.bot_depth))}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Turn</span>
                  <span className="text-white">
                    {chess?.turn() === 'w' ? 'White' : 'Black'}
                    {((chess?.turn() === 'w') === (game.player_color === 'white')) ? ' (You)' : ' (Bot)'}
                  </span>
                </div>
              </div>
            </div>

            {gameResult && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`rounded-2xl p-6 border ${
                  gameResult.result === 'win' 
                    ? 'bg-green-500/20 border-green-500/50' 
                    : gameResult.result === 'draw'
                    ? 'bg-yellow-500/20 border-yellow-500/50'
                    : 'bg-red-500/20 border-red-500/50'
                }`}
              >
                <h3 className={`text-xl font-bold text-center ${
                  gameResult.result === 'win' ? 'text-green-400' : 
                  gameResult.result === 'draw' ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  {gameResult.result === 'win' ? 'Victory!' : 
                   gameResult.result === 'draw' ? 'Draw!' : 'Defeat'}
                </h3>
                
                {gameResult.calibrationComplete ? (
                  <div className="mt-4 text-center">
                    <p className="text-slate-300">Calibration Complete!</p>
                    <p className="text-2xl font-bold text-blue-400 mt-2">
                      Rating: {gameResult.rating}
                    </p>
                  </div>
                ) : (
                  <button
                    onClick={() => { setGame(null); setChess(null); setGameResult(null); }}
                    className="w-full mt-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-white"
                  >
                    Next Game
                  </button>
                )}
              </motion.div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// P2P Game View Component
function P2PGameView({ user, token, refreshUser, chessRating }) {
  const [lobby, setLobby] = useState([]);
  const [activeGame, setActiveGame] = useState(null);
  const [chess, setChess] = useState(null);
  const [betAmount, setBetAmount] = useState(50);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [showHints, setShowHints] = useState(true);

  const fetchLobby = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/games/chess/p2p/lobby`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLobby(response.data.games || []);
    } catch (error) {
      console.error('Failed to fetch lobby:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchLobby();
    const interval = setInterval(fetchLobby, 5000);
    return () => clearInterval(interval);
  }, [fetchLobby]);

  // Poll for game updates when in waiting or active game
  useEffect(() => {
    if (!activeGame || !activeGame.game_id) return;
    if (activeGame.status !== 'active' && activeGame.status !== 'waiting') return;

    const pollGame = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/api/games/chess/p2p/${activeGame.game_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const gameData = response.data;
        
        // If game transitioned from waiting to active, initialize chess
        if (activeGame.status === 'waiting' && gameData.status === 'active') {
          setChess(new ChessJS(gameData.fen));
          toast.success('Opponent joined! Game starting...');
        }
        
        setActiveGame(gameData);
        
        // Update chess position if changed (for active games)
        if (gameData.status === 'active' && gameData.fen !== chess?.fen()) {
          setChess(new ChessJS(gameData.fen));
        }
        
        if (gameData.status === 'completed') {
          handleGameEnd(gameData);
        }
      } catch (error) {
        console.error('Poll error:', error);
        // If game not found (cancelled), go back to lobby
        if (error.response?.status === 404) {
          setActiveGame(null);
          toast.info('Game was cancelled');
        }
      }
    };

    setPolling(true);
    const interval = setInterval(pollGame, 1500);
    return () => { clearInterval(interval); setPolling(false); };
  }, [activeGame?.game_id, activeGame?.status, token]);

  const handleGameEnd = (gameData) => {
    const won = gameData.winner_id === user.user_id;
    const isDraw = !gameData.winner_id;
    
    if (won) {
      toast.success('You won!');
    } else if (isDraw) {
      toast.info('Game ended in a draw');
    } else {
      toast.error('You lost!');
    }
    
    refreshUser();
  };

  const createGame = async () => {
    if (betAmount < 10) return toast.error('Minimum bet is R$10');
    if (betAmount > (user?.site_balance || 0)) return toast.error('Insufficient balance');

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/games/chess/p2p/create`,
        { bet_amount: betAmount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setActiveGame({
        ...response.data,
        creator_id: user.user_id,
        creator_username: user.username
      });
      toast.success('Game created! Waiting for opponent...');
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create game');
    } finally {
      setLoading(false);
    }
  };

  const joinGame = async (gameId) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/games/chess/p2p/join`,
        { game_id: gameId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setChess(new ChessJS(response.data.fen));
      setActiveGame(response.data);
      toast.success('Game joined!');
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join game');
    } finally {
      setLoading(false);
    }
  };

  const makeMove = async (sourceSquare, targetSquare, piece) => {
    if (!activeGame || !chess || activeGame.status !== 'active') return false;

    const isWhiteTurn = chess.turn() === 'w';
    const playerIsWhite = activeGame.your_color === 'white' || 
                         activeGame.white_player_id === user.user_id;

    if (isWhiteTurn !== playerIsWhite) {
      toast.error("Not your turn");
      return false;
    }

    const move = chess.move({
      from: sourceSquare,
      to: targetSquare,
      promotion: piece?.[1]?.toLowerCase() || 'q'
    });

    if (!move) return false;
    chess.undo();

    try {
      const moveUci = `${sourceSquare}${targetSquare}${move.promotion || ''}`;
      const response = await axios.post(
        `${API_URL}/api/games/chess/p2p/move`,
        { game_id: activeGame.game_id, move: moveUci },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setChess(new ChessJS(response.data.fen));
      setActiveGame(prev => ({
        ...prev,
        fen: response.data.fen,
        white_time_remaining: response.data.white_time_remaining,
        black_time_remaining: response.data.black_time_remaining,
        status: response.data.game_over ? 'completed' : 'active',
        result: response.data.result,
        winner_id: response.data.winner_id
      }));

      if (response.data.game_over) {
        handleGameEnd(response.data);
      }

      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid move');
      return false;
    }
  };

  const resignGame = async () => {
    if (!activeGame) return;
    
    try {
      await axios.post(
        `${API_URL}/api/games/chess/p2p/${activeGame.game_id}/resign`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.info('You resigned');
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resign');
    }
  };

  const cancelGame = async () => {
    if (!activeGame) return;
    
    try {
      await axios.post(
        `${API_URL}/api/games/chess/p2p/${activeGame.game_id}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActiveGame(null);
      toast.success('Game cancelled, bet refunded');
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Waiting for opponent view
  if (activeGame && activeGame.status === 'waiting') {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-slate-900/50 rounded-2xl p-8 border border-slate-800 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-blue-500/20 flex items-center justify-center">
            <Users className="w-8 h-8 text-blue-500 animate-pulse" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Waiting for Opponent</h2>
          <p className="text-slate-400 mb-6">
            Bet: R${activeGame.bet_amount} | Your Rating: {chessRating?.rating}
          </p>
          <div className="flex justify-center gap-4">
            <button
              onClick={cancelGame}
              className="px-6 py-2 bg-red-600 hover:bg-red-500 rounded-lg text-white"
            >
              Cancel Game
            </button>
          </div>
          <p className="text-slate-500 text-sm mt-4">
            Only players with rating {Math.floor(chessRating?.rating * 0.75)} - {Math.ceil(chessRating?.rating * 1.25)} can join
          </p>
        </div>
      </div>
    );
  }

  // Active game view
  if (activeGame && activeGame.status === 'active') {
    const playerIsWhite = activeGame.your_color === 'white' || 
                         activeGame.white_player_id === user.user_id;
    const isPlayerTurn = chess && ((chess.turn() === 'w') === playerIsWhite);

    return (
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            {/* Opponent info */}
            <div className="bg-slate-900/50 rounded-xl p-3 mb-2 border border-slate-800 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center overflow-hidden">
                  {activeGame.opponent_avatar || activeGame.creator_avatar ? (
                    <img 
                      src={playerIsWhite ? activeGame.opponent_avatar : activeGame.creator_avatar} 
                      alt="" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-lg">♚</span>
                  )}
                </div>
                <div>
                  <p className="text-white font-medium">
                    {playerIsWhite ? activeGame.opponent_username : activeGame.creator_username}
                  </p>
                  <p className="text-slate-400 text-sm">
                    Rating: {playerIsWhite ? activeGame.opponent_rating : activeGame.creator_rating}
                  </p>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-lg font-mono text-lg ${
                !playerIsWhite ? 'bg-slate-800' : 'bg-blue-600'
              }`}>
                <Clock className="w-4 h-4 inline mr-1" />
                {formatTime(playerIsWhite ? activeGame.black_time_remaining : activeGame.white_time_remaining)}
              </div>
            </div>

            {/* Chess board */}
            <div className="bg-slate-900/50 rounded-2xl p-4 border border-slate-800">
              <div className="flex justify-end mb-2">
                <HintsToggle enabled={showHints} onChange={setShowHints} />
              </div>
              <GameChessboard
                chess={chess}
                onMove={makeMove}
                orientation={playerIsWhite ? 'white' : 'black'}
                showHints={showHints}
                disabled={activeGame.status === 'completed'}
              />
            </div>

            {/* Player info */}
            <div className="bg-slate-900/50 rounded-xl p-3 mt-2 border border-slate-800 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center overflow-hidden">
                  {user.roblox_avatar_url ? (
                    <img src={user.roblox_avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-lg">{playerIsWhite ? '♔' : '♚'}</span>
                  )}
                </div>
                <div>
                  <p className="text-white font-medium">{user.username} (You)</p>
                  <p className="text-slate-400 text-sm">Rating: {chessRating?.rating}</p>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-lg font-mono text-lg ${
                playerIsWhite ? 'bg-slate-800' : 'bg-blue-600'
              }`}>
                <Clock className="w-4 h-4 inline mr-1" />
                {formatTime(playerIsWhite ? activeGame.white_time_remaining : activeGame.black_time_remaining)}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-slate-900/50 rounded-2xl p-6 border border-slate-800">
              <h3 className="text-lg font-semibold text-white mb-4">Game Info</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-400">Bet</span>
                  <span className="text-green-400 font-bold">R${activeGame.bet_amount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Pot</span>
                  <span className="text-yellow-400 font-bold">R${activeGame.bet_amount * 2}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Your Color</span>
                  <span className="text-white">{playerIsWhite ? '♔ White' : '♚ Black'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Turn</span>
                  <span className={isPlayerTurn ? 'text-green-400' : 'text-slate-400'}>
                    {isPlayerTurn ? 'Your move' : 'Opponent\'s move'}
                  </span>
                </div>
              </div>

              <button
                onClick={resignGame}
                className="w-full mt-6 py-2 bg-red-600/20 hover:bg-red-600/40 border border-red-500/50 rounded-lg text-red-400 flex items-center justify-center gap-2"
              >
                <Flag className="w-4 h-4" /> Resign
              </button>
            </div>

            {/* Move history could go here */}
            <div className="bg-slate-900/50 rounded-2xl p-4 border border-slate-800">
              <h4 className="text-sm font-semibold text-slate-400 mb-2">Moves</h4>
              <div className="max-h-40 overflow-y-auto text-sm text-slate-300 font-mono">
                {activeGame.moves?.map((move, i) => (
                  <span key={i} className={i % 2 === 0 ? 'text-white' : 'text-slate-400'}>
                    {i % 2 === 0 ? `${Math.floor(i/2) + 1}. ` : ''}{move}{' '}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Completed game view
  if (activeGame && activeGame.status === 'completed') {
    const won = activeGame.winner_id === user.user_id;
    const isDraw = !activeGame.winner_id;

    return (
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className={`rounded-2xl p-8 border text-center ${
            won ? 'bg-green-500/20 border-green-500/50' :
            isDraw ? 'bg-yellow-500/20 border-yellow-500/50' :
            'bg-red-500/20 border-red-500/50'
          }`}
        >
          <Trophy className={`w-16 h-16 mx-auto mb-4 ${
            won ? 'text-green-400' : isDraw ? 'text-yellow-400' : 'text-red-400'
          }`} />
          <h2 className={`text-3xl font-bold mb-2 ${
            won ? 'text-green-400' : isDraw ? 'text-yellow-400' : 'text-red-400'
          }`}>
            {won ? 'Victory!' : isDraw ? 'Draw!' : 'Defeat'}
          </h2>
          <p className="text-slate-300 mb-4">{activeGame.result}</p>
          {won && (
            <p className="text-2xl font-bold text-green-400">
              +R${(activeGame.bet_amount * 2 * 0.95).toFixed(2)}
            </p>
          )}
          <button
            onClick={() => setActiveGame(null)}
            className="mt-6 px-8 py-3 bg-blue-600 hover:bg-blue-500 rounded-xl text-white font-bold"
          >
            Play Again
          </button>
        </motion.div>
      </div>
    );
  }

  // Lobby view
  return (
    <div className="max-w-5xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Create game panel */}
        <div className="bg-slate-900/50 rounded-2xl p-6 border border-slate-800">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Swords className="text-yellow-500" /> Create Game
          </h2>
          
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Trophy className="w-4 h-4 text-yellow-500" />
              <span className="text-slate-300">Your Rating: <span className="text-yellow-400 font-bold">{chessRating?.rating}</span></span>
            </div>
            <p className="text-slate-500 text-xs">
              Can match with: {Math.floor(chessRating?.rating * 0.75)} - {Math.ceil(chessRating?.rating * 1.25)}
            </p>
          </div>

          <div className="mb-4">
            <label className="text-slate-400 text-sm mb-2 block">Bet Amount</label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
              <input
                type="number"
                value={betAmount}
                onChange={(e) => setBetAmount(Number(e.target.value))}
                className="w-full bg-slate-800/50 border border-slate-700 rounded-xl pl-10 pr-4 py-3 text-white"
                min={10}
              />
            </div>
            <div className="flex gap-2 mt-2">
              {[50, 100, 250, 500].map((amt) => (
                <button
                  key={amt}
                  onClick={() => setBetAmount(amt)}
                  className="flex-1 py-1 bg-slate-800 hover:bg-slate-700 rounded-lg text-sm text-slate-300"
                >
                  {amt}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={createGame}
            disabled={loading || !user}
            className="w-full py-3 bg-yellow-600 hover:bg-yellow-500 disabled:bg-slate-700 rounded-xl text-white font-bold transition-colors"
          >
            {loading ? 'Creating...' : 'Create Game'}
          </button>
        </div>

        {/* Lobby list */}
        <div className="lg:col-span-2 bg-slate-900/50 rounded-2xl p-6 border border-slate-800">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Users className="text-blue-500" /> Available Games
          </h2>

          {lobby.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 mx-auto text-slate-600 mb-4" />
              <p className="text-slate-400">No games available in your skill range</p>
              <p className="text-slate-500 text-sm mt-2">Create a game and wait for an opponent!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {lobby.map((game) => (
                <motion.div
                  key={game.game_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-slate-800/50 rounded-xl p-4 border border-slate-700 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-slate-700 overflow-hidden">
                      {game.creator_avatar ? (
                        <img src={game.creator_avatar} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-2xl">♔</div>
                      )}
                    </div>
                    <div>
                      <p className="text-white font-medium">{game.creator_username}</p>
                      <p className="text-slate-400 text-sm">Rating: {game.creator_rating}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-green-400 font-bold">R${game.bet_amount}</p>
                    <button
                      onClick={() => joinGame(game.game_id)}
                      disabled={loading}
                      className="mt-1 px-4 py-1 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-600 rounded-lg text-white text-sm"
                    >
                      Join
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800 text-center">
          <p className="text-slate-400 text-sm">Games Played</p>
          <p className="text-2xl font-bold text-white">{chessRating?.games_played || 0}</p>
        </div>
        <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800 text-center">
          <p className="text-slate-400 text-sm">Wins</p>
          <p className="text-2xl font-bold text-green-400">{chessRating?.wins || 0}</p>
        </div>
        <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800 text-center">
          <p className="text-slate-400 text-sm">Losses</p>
          <p className="text-2xl font-bold text-red-400">{chessRating?.losses || 0}</p>
        </div>
        <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800 text-center">
          <p className="text-slate-400 text-sm">Draws</p>
          <p className="text-2xl font-bold text-yellow-400">{chessRating?.draws || 0}</p>
        </div>
      </div>
    </div>
  );
}

// Main Chess Component
export default function Chess() {
  const { user, token, refreshUser } = useAuth();
  const [chessRating, setChessRating] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'RBX.Casino | Chess';
  }, []);

  const fetchRating = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `${API_URL}/api/games/chess/p2p/rating`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setChessRating(response.data);
    } catch (error) {
      console.error('Failed to fetch rating:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchRating();
  }, [fetchRating]);

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto text-center py-20">
        <Crown className="w-20 h-20 mx-auto text-yellow-500 mb-6" />
        <h1 className="text-3xl font-bold text-white mb-4">P2P Chess</h1>
        <p className="text-slate-400 mb-8">
          Play real chess against other players for Robux. Skill-based matchmaking ensures fair games.
        </p>
        <a href="/login" className="px-8 py-3 bg-blue-600 hover:bg-blue-500 rounded-xl text-white font-bold inline-block">
          Login to Play
        </a>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Show calibration if not calibrated
  if (!chessRating?.calibrated) {
    return (
      <CalibrationView
        user={user}
        token={token}
        chessRating={chessRating}
        onComplete={fetchRating}
      />
    );
  }

  // Show P2P view if calibrated
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
          <Crown className="text-yellow-500" /> P2P Chess
        </h1>
        <div className="flex items-center gap-2 bg-slate-800/50 px-4 py-2 rounded-xl">
          <Trophy className="w-5 h-5 text-yellow-500" />
          <span className="text-white font-bold">{chessRating?.rating}</span>
          <span className="text-slate-400 text-sm">Rating</span>
        </div>
      </div>

      <P2PGameView
        user={user}
        token={token}
        refreshUser={refreshUser}
        chessRating={chessRating}
      />
    </div>
  );
}
